import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/common/Toast';
import { restaurantService, type Restaurant } from '@/services/restaurant';
import { stockService } from '@/services/stock';
import { cartService } from '@/services/cart';
import { tossPaymentsService } from '@/services/tossPayments';
import { useCartStore } from '@/store/cart';
import type { CheckoutState } from './checkout.types';
import { useCheckoutValidation } from './useCheckoutValidation';
import { usePaymentWidget } from './usePaymentWidget';
import { CheckoutSummary } from './CheckoutSummary';

export function CheckoutPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast, ToastContainer } = useToast();
  
  const [checkoutData, setCheckoutData] = useState<CheckoutState | null>(null);
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);

  // 커스텀 훅으로 검증 로직 관리
  const {
    isValidating,
    validationErrors,
    setValidationErrors,
    checkoutToken,
    orderId
  } = useCheckoutValidation({
    checkoutData,
    restaurant,
    userId: user?.id
  });

  // 커스텀 훅으로 결제 위젯 관리
  const {
    isWidgetReady,
    isRecovering,
    isResetting,
    isProcessingPayment,
    setIsProcessingPayment,
    handlePaymentError
  } = usePaymentWidget({
    checkoutData,
    orderId,
    validationErrors,
    userId: user?.id,
    showToast
  });

  // 로그인 상태 확인
  useEffect(() => {
    if (!user) {
      navigate('/login', { replace: true });
    }
  }, [user, navigate]);

  // 장바구니에서 전달받은 데이터 확인
  useEffect(() => {
    if (!user) return;
    
    if (!location.state) {
      showToast('결제 정보가 없습니다');
      navigate('/cart');
      return;
    }

    setCheckoutData(location.state as CheckoutState);
  }, [location.state, navigate, showToast, user]);


  // 매장 정보 조회
  useEffect(() => {
    const fetchRestaurant = async () => {
      if (!checkoutData?.storeId) return;

      try {
        const result = await restaurantService.getRestaurantById(checkoutData.storeId, true);
        if (result.success && result.data) {
          setRestaurant(result.data);
        } else {
          setValidationErrors(prev => [...prev, '매장 정보를 불러올 수 없습니다']);
        }
      } catch (error) {
        console.error('Error fetching restaurant:', error);
        setValidationErrors(prev => [...prev, '매장 정보 조회 중 오류가 발생했습니다']);
      }
    };

    fetchRestaurant();
  }, [checkoutData?.storeId]);




  const handlePayment = async () => {
    // 중복 실행 방지
    if (isProcessingPayment) {
      return;
    }
    
    if (!checkoutData || !user || !orderId || !checkoutToken || validationErrors.length > 0) {
      showToast('결제 조건을 확인해주세요');
      return;
    }
    
    // 위젯 준비 상태 재확인
    if (!isWidgetReady) {
      showToast('결제 위젯 로딩 중입니다. 잠시만 기다려주세요');
      return;
    }

    setIsProcessingPayment(true);

    try {
      // 포인트 전액 결제 (0원) 분기: PG 없이 서버에서 완료 처리
      if (checkoutData.finalPrice <= 0) {
        const result = await tossPaymentsService.completePointPayment(orderId);
        if (!result.success) {
          throw new Error(result.error || '포인트 결제 처리에 실패했습니다.');
        }

        // 장바구니 정리(서버/로컬)
        try {
          const currentCart = await cartService.getCurrentCart(user.id);
          if (currentCart) {
            await cartService.clearCart(currentCart.id);
          }
          useCartStore.getState().clearCart(user.id);
        } catch (cleanupError) {
          console.warn('Cart cleanup after point payment failed:', cleanupError);
        }

        showToast('포인트 전액 결제로 주문이 완료되었습니다');
        navigate('/buyer/orders', { replace: true });
        return;
      }

      // 최종 재고 검증
      const finalStockValidation = await stockService.validateCartStock(
        checkoutData.items.map(item => ({
          productId: item.productId,
          quantity: item.quantity,
          productName: item.productName
        })),
        checkoutData.orderType
      );

      if (!finalStockValidation.isValid) {
        showToast('재고가 변경되어 결제할 수 없습니다');
        setTimeout(() => {
          navigate('/cart');
        }, 2000);
        return;
      }

      // 주문명 생성
      const orderName = checkoutData.items.length === 1 
        ? checkoutData.items[0].productName
        : `${checkoutData.items[0].productName} 외 ${checkoutData.items.length - 1}건`;

      console.log('💳 TossPayments 결제 요청 시작', {
        orderId,
        orderName,
        amount: checkoutData.finalPrice,
        customerEmail: user.email,
        successUrl: `${window.location.origin}/payment/success`,
        failUrl: `${window.location.origin}/payment/fail`
      });

      // TossPayments 결제 요청
      await tossPaymentsService.requestPayment({
        orderId,
        orderName,
        amount: checkoutData.finalPrice,
        customerEmail: user.email,
        customerName: user.name || user.email?.split('@')[0] || 'Guest',
        customerMobilePhone: user.phone || undefined,
        cartId: await cartService.getCurrentCart(user.id).then(cart => cart?.id || ''),
        checkoutToken,
        orderType: checkoutData.orderType,
        restaurantId: checkoutData.storeId,
        userId: user.id
      });

      console.log('✅ TossPayments requestPayment 호출 완료 - 결제창으로 전환됨');

    } catch (error: any) {
      console.error('Payment error:', error);
      await handlePaymentError(error);
    } finally {
      setIsProcessingPayment(false);
    }
  };



  if (!checkoutData || !user) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="sticky top-0 bg-white z-10 border-b border-gray-200">
        <div className="flex items-center justify-center px-4 py-3 relative">
          <button
            onClick={() => navigate(-1)}
            className="absolute left-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <img
              src="/icons/icon_back.svg"
              alt="뒤로가기"
              width="24"
              height="24"
            />
          </button>
          <h1 className="text-2xl font-bold">주문 확인 및 결제</h1>
        </div>
      </div>

      {/* Checkout Summary Component */}
      <CheckoutSummary 
        checkoutData={checkoutData}
        validationErrors={validationErrors}
        orderId={orderId}
        isRecovering={isRecovering}
        isWidgetReady={isWidgetReady}
      />

      {/* Fixed Bottom Button */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t">
        <button
          onClick={handlePayment}
          disabled={isProcessingPayment || isValidating || validationErrors.length > 0 || !isWidgetReady || isRecovering || isResetting}
          className={`w-full py-4 rounded-xl font-semibold transition-colors ${
            isProcessingPayment || isValidating || validationErrors.length > 0 || !isWidgetReady || isRecovering || isResetting
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-green-500 text-white hover:bg-green-600'
          }`}
        >
          {isProcessingPayment ? (
            <div className="flex items-center justify-center gap-2">
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              <span>결제 처리 중...</span>
            </div>
          ) : isResetting ? (
            <div className="flex items-center justify-center gap-2">
              <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              <span>결제 환경 재설정 중...</span>
            </div>
          ) : isRecovering ? (
            <div className="flex items-center justify-center gap-2">
              <div className="w-5 h-5 border-2 border-green-500 border-t-transparent rounded-full animate-spin"></div>
              <span>결제 환경 복구 중...</span>
            </div>
          ) : isValidating ? (
            '검증 중...'
          ) : !isWidgetReady && !validationErrors.length ? (
            '결제 준비 중...'
          ) : (
            `${checkoutData.finalPrice.toLocaleString()}원 결제하기`
          )}
        </button>
      </div>

      <ToastContainer />
    </div>
  );
}
